import {
  BarChartOutlined,
  CloseOutlined,
  DownloadOutlined,
  HolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button, Col, DatePicker, Empty, Input, Row, Select, Space, Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Client } from "../../core/api-client";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import {
  API_REPORT_METADATA,
  API_REPORT_QUERY,
  API_SAVED_REPORTS,
  ROUTE_REPORTS,
} from "../../shared/routes/private.routes";
import { exportCsv } from "../../shared/utils/export-csv";
import { formatReportJsonValue } from "../../shared/utils/report-value-format";
import { formatDate, showNotification } from "../../shared/utils/utils";
import "./report-builder.less";

type ReportColumn = {
  key: string;
  label: string;
  type: string;
};

type ReportDataset = {
  key: "staff" | "project" | "milestone";
  label: string;
  defaultColumns: string[];
  columns: ReportColumn[];
  dateFilterColumns?: ReportColumn[];
};

const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100"];
const REPORT_DATE_FORMAT = "DD-MMM-YYYY";
const API_DATE_FORMAT = "YYYY-MM-DD";
const FY_OPTIONS = [
  { label: "Current F.Y", value: "current" },
  { label: "Previous F.Y", value: "previous" },
];
const getFinancialYearRange = (fyKey: string): [Dayjs, Dayjs] => {
  const today = dayjs();
  const currentFyStartYear = today.month() >= 3 ? today.year() : today.year() - 1;
  const offset = fyKey === "next" ? 1 : fyKey === "previous" ? -1 : 0;
  const startYear = currentFyStartYear + offset;

  return [
    dayjs(`${startYear}-04-01`, API_DATE_FORMAT),
    dayjs(`${startYear + 1}-03-31`, API_DATE_FORMAT),
  ];
};

const getFinancialYearKeyFromRange = (from?: string, to?: string) => {
  if (!from || !to) return undefined;

  return FY_OPTIONS.find((option) => {
    const [fyFrom, fyTo] = getFinancialYearRange(option.value);
    return (
      fyFrom.format(API_DATE_FORMAT) === from &&
      fyTo.format(API_DATE_FORMAT) === to
    );
  })?.value;
};

const getReportColumnWidth = (column: ReportColumn) => {
  if (column.key.toLowerCase().includes("email")) return 260;
  if (column.type === "json") return 360;
  if (column.type === "datetime") return 210;
  if (column.type === "date") return 180;
  if (column.label.length > 24) return 240;
  return 180;
};

const formatReportValue = (value: any, column: ReportColumn) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if ((column.type === "date" || column.type === "datetime") && value) {
    return formatDate(value, "DD-MMM-YYYY");
  }
  if (column.type === "json") return formatReportJsonValue(value, column.key) || "-";
  if (typeof value === "object") return formatReportJsonValue(value, column.key) || "-";
  return String(value);
};

const ReportBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [datasets, setDatasets] = useState<ReportDataset[]>([]);
  const [reportName, setReportName] = useState("");
  const [datasetKey, setDatasetKey] = useState<string>();
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [loadingSavedReport, setLoadingSavedReport] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(null);
  const [financialYear, setFinancialYear] = useState<string>();
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [dateFilterField, setDateFilterField] = useState<string>();

  const activeDataset = useMemo(
    () => datasets.find((dataset) => dataset.key === datasetKey),
    [datasets, datasetKey]
  );

  const selectedColumns = useMemo(() => {
    if (!activeDataset) return [];
    const columnMap = new Map(
      activeDataset.columns.map((column) => [column.key, column])
    );

    return selectedColumnKeys
      .map((key) => columnMap.get(key))
      .filter(Boolean) as ReportColumn[];
  }, [activeDataset, selectedColumnKeys]);

  const availableColumns = useMemo(() => {
    if (!activeDataset) return [];
    const normalizedSearch = search.trim().toLowerCase();

    return activeDataset.columns.filter((column) => {
      if (!normalizedSearch) return true;
      return (
        column.label.toLowerCase().includes(normalizedSearch) ||
        column.key.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [activeDataset, search]);

  const dateFilterColumns = activeDataset?.dateFilterColumns || [];
  const hasDateFilterColumns = dateFilterColumns.length > 0;
  const showDateFilters = hasDateFilterColumns;

  const reportDateFilter = useMemo(() => {
    if (!dateFilterField) return undefined;
    if (!fromDate || !toDate) return undefined;

    return {
      field: dateFilterField,
      fromDate: fromDate.format(API_DATE_FORMAT),
      toDate: toDate.format(API_DATE_FORMAT),
    };
  }, [dateFilterField, fromDate, toDate]);

  const isInvalidDateRange = Boolean(
    fromDate && toDate && fromDate.isAfter(toDate, "day")
  );

  const previewColumns = selectedColumns;

  const tableColumns = useMemo(
    () =>
      previewColumns.map((column) => {
        const width = getReportColumnWidth(column);

        return {
          title: column.label,
          dataIndex: column.key,
          key: column.key,
          width,
          align: "left" as const,
          ellipsis: true,
          onHeaderCell: () => ({
            style: {
              minWidth: width,
              width,
              textAlign: "left" as const,
            },
          }),
          onCell: () => ({
            className: column.type === "json" ? "report-builder-json-cell" : undefined,
            style: {
              minWidth: width,
              width,
              maxWidth: width,
              textAlign: "left" as const,
            },
          }),
          render: (value: any) => {
            const formattedValue = formatReportValue(value, column);

            if (column.type !== "json" || formattedValue === "-") {
              return formattedValue;
            }

            return (
              <Tooltip title={formattedValue}>
                <span>{formattedValue}</span>
              </Tooltip>
            );
          },
        };
      }),
    [previewColumns]
  );

  const tableWidth = useMemo(
    () =>
      previewColumns.reduce(
        (total, column) => total + getReportColumnWidth(column),
        0
      ),
    [previewColumns]
  );

  const dataSource = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        __rowId: `${currentPage}-${index}`,
      })),
    [rows, currentPage]
  );

  const loadMetadata = useCallback(async () => {
    setLoadingMetadata(true);
    try {
      const response = await Client.get(API_REPORT_METADATA);
      const nextDatasets = response?.data?.datasets || [];
      setDatasets(nextDatasets);

      if (nextDatasets.length > 0 && !reportId) {
        setDatasetKey(nextDatasets[0].key);
        setSelectedColumnKeys([]);
      }
    } catch {
      showNotification("Failed to load report datasets", "error");
    } finally {
      setLoadingMetadata(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const loadSavedReport = useCallback(async () => {
    if (!reportId) return;

    setLoadingSavedReport(true);
    try {
      const response = await Client.get(`${API_SAVED_REPORTS}/${reportId}`);
      const savedReport = response?.data;
      setReportName(savedReport?.reportName || "");
      setDatasetKey(savedReport?.dataset);
      setSelectedColumnKeys(savedReport?.columns || []);
      setDateFilterField(savedReport?.dateFilter?.field);
      setFinancialYear(
        getFinancialYearKeyFromRange(
          savedReport?.dateFilter?.fromDate,
          savedReport?.dateFilter?.toDate
        )
      );
      setFromDate(
        savedReport?.dateFilter?.fromDate
          ? dayjs(savedReport.dateFilter.fromDate, API_DATE_FORMAT)
          : null
      );
      setToDate(
        savedReport?.dateFilter?.toDate
          ? dayjs(savedReport.dateFilter.toDate, API_DATE_FORMAT)
          : null
      );
      setRows([]);
      setTotalRows(0);
      setCurrentPage(1);
    } catch {
      showNotification("Failed to load saved report", "error");
      navigate(ROUTE_REPORTS);
    } finally {
      setLoadingSavedReport(false);
    }
  }, [navigate, reportId]);

  useEffect(() => {
    loadSavedReport();
  }, [loadSavedReport]);

  const loadReport = useCallback(
    async (page = currentPage, nextPageSize = pageSize) => {
      if (!datasetKey || selectedColumnKeys.length === 0) {
        showNotification("Please select a dataset and at least one column.", "warning");
        return;
      }
      if (dateFilterField && (!fromDate || !toDate)) {
        showNotification("Please select both From Date and To Date.", "warning");
        return;
      }
      if (isInvalidDateRange) {
        showNotification("From Date cannot be after To Date.", "warning");
        return;
      }

      setLoadingData(true);
      try {
        const response = await Client.post(
          API_REPORT_QUERY,
          {
            dataset: datasetKey,
            columns: selectedColumnKeys,
            dateFilter: reportDateFilter,
          },
          {
            params: {
              page,
              limit: nextPageSize,
            },
          }
        );
        setRows(response?.data?.data || []);
        setTotalRows(response?.data?.total || 0);
        setCurrentPage(response?.data?.page || page);
        setPageSize(response?.data?.limit || nextPageSize);
      } catch {
        showNotification("Failed to load report data", "error");
      } finally {
        setLoadingData(false);
      }
    },
    [
      currentPage,
      dateFilterField,
      datasetKey,
      fromDate,
      pageSize,
      reportDateFilter,
      selectedColumnKeys,
      isInvalidDateRange,
      toDate,
    ]
  );

  const handleDatasetChange = (nextDatasetKey: string) => {
    setDatasetKey(nextDatasetKey);
    setSelectedColumnKeys([]);
    setRows([]);
    setTotalRows(0);
    setCurrentPage(1);
    setSearch("");
    setDateFilterField(undefined);
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      showNotification("Please enter report name.", "warning");
      return;
    }
    if (!datasetKey || selectedColumnKeys.length === 0) {
      showNotification("Please select a dataset and at least one column.", "warning");
      return;
    }
    if (dateFilterField && (!fromDate || !toDate)) {
      showNotification("Please select both From Date and To Date.", "warning");
      return;
    }
    if (isInvalidDateRange) {
      showNotification("From Date cannot be after To Date.", "warning");
      return;
    }

    setSavingReport(true);
    try {
      const payload = {
        reportName: reportName.trim(),
        dataset: datasetKey,
        columns: selectedColumnKeys,
        dateFilter: reportDateFilter,
      };
      const response = reportId
        ? await Client.put(`${API_SAVED_REPORTS}/${reportId}`, payload)
        : await Client.post(API_SAVED_REPORTS, payload);
      showNotification("Report saved successfully", "success");

      if (!reportId && response?.data?.reportId) {
        navigate(`${ROUTE_REPORTS}/edit/${response.data.reportId}`, { replace: true });
      }
    } catch {
      showNotification("Failed to save report", "error");
    } finally {
      setSavingReport(false);
    }
  };

  const handleFinancialYearChange = (nextFinancialYear?: string) => {
    if (!nextFinancialYear) {
      setFinancialYear(undefined);
      setFromDate(null);
      setToDate(null);
      setRows([]);
      setTotalRows(0);
      setCurrentPage(1);
      return;
    }

    const [nextFromDate, nextToDate] = getFinancialYearRange(nextFinancialYear);
    setFinancialYear(nextFinancialYear);
    setFromDate(nextFromDate);
    setToDate(nextToDate);
    setRows([]);
    setTotalRows(0);
    setCurrentPage(1);
  };

  const addColumn = (columnKey: string) => {
    setSelectedColumnKeys((current) =>
      current.includes(columnKey) ? current : [...current, columnKey]
    );
  };

  const removeColumn = (columnKey: string) => {
    setSelectedColumnKeys((current) => current.filter((key) => key !== columnKey));
  };

  const addAllColumns = () => {
    if (!activeDataset) return;
    setSelectedColumnKeys(activeDataset.columns.map((column) => column.key));
  };

  const clearColumns = () => {
    setSelectedColumnKeys([]);
    setRows([]);
    setTotalRows(0);
  };

  const moveSelectedColumn = (targetColumnKey: string) => {
    if (!draggedColumnKey || draggedColumnKey === targetColumnKey) return;

    setSelectedColumnKeys((current) => {
      const next = [...current];
      const fromIndex = next.indexOf(draggedColumnKey);
      const toIndex = next.indexOf(targetColumnKey);

      if (toIndex === -1) return current;

      if (fromIndex === -1) {
        next.splice(toIndex, 0, draggedColumnKey);
        return next;
      }

      next.splice(fromIndex, 1);
      const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      next.splice(insertIndex, 0, draggedColumnKey);
      return next;
    });
  };

  const dropColumnAtEnd = () => {
    if (!draggedColumnKey) return;
    setSelectedColumnKeys((current) =>
      current.includes(draggedColumnKey) ? current : [...current, draggedColumnKey]
    );
  };

  const handleExport = async () => {
    if (!datasetKey || selectedColumnKeys.length === 0) {
      showNotification("Please select a dataset and at least one column.", "warning");
      return;
    }

    if (!totalRows) {
      showNotification("Load report data before exporting.", "warning");
      return;
    }
    if (dateFilterField && (!fromDate || !toDate)) {
      showNotification("Please select both From Date and To Date.", "warning");
      return;
    }
    if (isInvalidDateRange) {
      showNotification("From Date cannot be after To Date.", "warning");
      return;
    }

    setExportingData(true);
    try {
      const response = await Client.post(
        API_REPORT_QUERY,
        {
          dataset: datasetKey,
          columns: selectedColumnKeys,
          dateFilter: reportDateFilter,
        },
        {
          params: {
            page: 1,
            limit: totalRows,
          },
        }
      );
      const exportRows = response?.data?.data || [];

      if (!exportRows.length) {
        showNotification("No report data found to export.", "warning");
        return;
      }

      exportCsv(`${datasetKey || "dataset"}_report.csv`, exportRows, previewColumns);
    } catch {
      showNotification("Failed to export report data", "error");
    } finally {
      setExportingData(false);
    }
  };

  const renderColumnRow = (
    column: ReportColumn,
    selected: boolean,
    selectedList = false
  ) => (
    <div
      key={column.key}
      className={`report-builder-column-row${selected ? " is-selected" : ""}${
        draggedColumnKey === column.key ? " is-dragging" : ""
      }`}
      draggable
      onDragStart={() => setDraggedColumnKey(column.key)}
      onDragOver={(event) => {
        if (selectedList) event.preventDefault();
      }}
      onDrop={() => selectedList && moveSelectedColumn(column.key)}
      onDragEnd={() => setDraggedColumnKey(null)}
    >
      <Space size={8} style={{ minWidth: 0 }}>
        {selectedList && <HolderOutlined style={{ color: "#6b7280" }} />}
        <span className="report-builder-column-name" title={column.label}>
          {column.label}
        </span>
      </Space>
      <Space size={8}>
        <span className="report-builder-column-type">{column.type}</span>
        {selectedList ? (
          <Tooltip title="Remove">
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => removeColumn(column.key)}
            />
          </Tooltip>
        ) : (
          <Tooltip title={selected ? "Already selected" : "Add"}>
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              disabled={selected}
              onClick={() => addColumn(column.key)}
            />
          </Tooltip>
        )}
      </Space>
    </div>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <BarChartOutlined />
              </div>
              <h1 className="app-page-title">
                {reportId ? "Edit Report" : "Create Report"}
              </h1>
            </div>
            <p className="app-page-subtitle">
              Build staff, project, and milestone datasets from selectable columns.
            </p>
          </Col>
          <Col>
            <Space wrap>
              <Button onClick={() => navigate(ROUTE_REPORTS)}>
                Manage Reports
              </Button>
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveReport}
                loading={savingReport}
              >
                Save Report
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadReport(1, pageSize)}
                loading={loadingData || loadingSavedReport}
              >
                Load Data
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exportingData}
                disabled={!totalRows}
              >
                Export CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="app-filter-bar report-builder-filter-bar">
        <div className="report-builder-controls-row">
          <label className="report-builder-control report-builder-control-name">
            <span className="report-builder-control-label">Report Name</span>
            <Input
              value={reportName}
              onChange={(event) => setReportName(event.target.value)}
              placeholder="Enter report name"
            />
          </label>
          <label className="report-builder-control report-builder-control-dataset">
            <span className="report-builder-control-label">Dataset</span>
            <Select
              value={datasetKey}
              loading={loadingMetadata}
              onChange={handleDatasetChange}
              options={datasets.map((dataset) => ({
                label: dataset.label,
                value: dataset.key,
              }))}
            />
          </label>
          {showDateFilters && (
            <>
              <label className="report-builder-control report-builder-control-fy">
                <span className="report-builder-control-label">Financial Year</span>
                <Select
                  allowClear
                  value={financialYear}
                  onChange={handleFinancialYearChange}
                  placeholder="Select F.Y"
                  options={FY_OPTIONS}
                />
              </label>
              <label className="report-builder-control report-builder-control-filter-date">
                <span className="report-builder-control-label">Filter Date By</span>
                <Select
                  allowClear
                  value={dateFilterField}
                  onChange={(value) => {
                    setDateFilterField(value);
                    setRows([]);
                    setTotalRows(0);
                    setCurrentPage(1);
                  }}
                  placeholder="Choose date field"
                  options={dateFilterColumns.map((column) => ({
                    label: column.label,
                    value: column.key,
                  }))}
                />
              </label>
              <label className="report-builder-control report-builder-control-date">
                <span className="report-builder-control-label">From Date</span>
                <DatePicker
                  value={fromDate}
                  format={REPORT_DATE_FORMAT}
                  onChange={(date) => {
                    setFromDate(date);
                    setFinancialYear(undefined);
                    setRows([]);
                    setTotalRows(0);
                    setCurrentPage(1);
                  }}
                  placeholder="From Date"
                />
              </label>
              <label className="report-builder-control report-builder-control-date">
                <span className="report-builder-control-label">To Date</span>
                <DatePicker
                  value={toDate}
                  format={REPORT_DATE_FORMAT}
                  onChange={(date) => {
                    setToDate(date);
                    setFinancialYear(undefined);
                    setRows([]);
                    setTotalRows(0);
                    setCurrentPage(1);
                  }}
                  placeholder="To Date"
                />
              </label>
            </>
          )}
          {!!selectedColumns.length && (
            <Tag color="blue" className="report-builder-selected-count">
              {selectedColumns.length} columns selected
            </Tag>
          )}
        </div>
      </div>

      <div className="report-builder-shell">
        <div className="report-builder-panel">
          <div className="report-builder-panel-header">
            <h2 className="report-builder-panel-title">Available Columns</h2>
            <Button size="small" onClick={addAllColumns} disabled={!activeDataset}>
              Add All
            </Button>
          </div>
          <div style={{ padding: "10px 10px 0" }}>
            <Input.Search
              allowClear
              className="report-builder-column-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search columns"
              style={{ width: "calc(100% - 22px)" }}
            />
          </div>
          <div className="report-builder-list">
            {availableColumns.length ? (
              availableColumns.map((column) =>
                renderColumnRow(column, selectedColumnKeys.includes(column.key))
              )
            ) : (
              <div className="report-builder-empty">No columns found.</div>
            )}
          </div>
        </div>

        <div className="report-builder-panel">
          <div className="report-builder-panel-header">
            <h2 className="report-builder-panel-title">Selected Columns</h2>
            <Button size="small" onClick={clearColumns} disabled={!selectedColumns.length}>
              Clear
            </Button>
          </div>
          <div
            className="report-builder-list"
            onDragOver={(event) => event.preventDefault()}
            onDrop={dropColumnAtEnd}
          >
            {selectedColumns.length ? (
              selectedColumns.map((column) => renderColumnRow(column, true, true))
            ) : (
              <div className="report-builder-empty">
                Add columns from the left to build a dataset.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="report-builder-table-wrap">
        {!rows.length && !loadingData ? (
          <Empty
            description="Choose columns and load data to preview the report."
            style={{ padding: 48 }}
          />
        ) : (
          <DataTableComponent
            rowKey="__rowId"
            pagination
            loading={loadingData}
            dataSource={dataSource}
            columns={tableColumns}
            defaultPage={1}
            currentPage={currentPage}
            totalRows={totalRows}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            tableWidth={Math.max(tableWidth, 900)}
            scrollY="calc(100vh - 430px)"
            onPageChange={(page: number) => loadReport(page, pageSize)}
            onPageSizeChange={(page: number, nextPageSize: number) =>
              loadReport(page, nextPageSize)
            }
          />
        )}
      </div>
    </div>
  );
};

export default ReportBuilderPage;
